import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Link,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Language as WebsiteIcon,
  SmartToy as BotIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

// Business type mapping
const getBusinessTypeName = (type) => {
  const businessTypes = {
    1: "E-commerce",
    2: "Service Business",
    3: "Educational",
    4: "Healthcare",
    5: "Technology",
    6: "Other",
  };
  return businessTypes[type] || "General";
};

// Widget Card Component
const WidgetCard = ({
  data: widget,
  selected: isSelected,
  onSelect,
  onDelete,
  index,
  spaceId,
}) => {
  const serialNumber = index + 1;

  const handleCardClick = (event) => {
    // Prevent selection when clicking on action buttons
    if (event.target.closest("button")) {
      return;
    }
    onSelect(widget.id);
  };

  const handleEditClick = (event) => {
    event.stopPropagation();
    // Navigate to edit page - you might need to adjust this URL structure
    window.location.href = `/widget/add-channel-connect?id=${widget.id}${
      spaceId ? `&spaceId=${spaceId}` : ""
    }`;
  };

  const handleDeleteClick = (event) => {
    event.stopPropagation();
    onDelete(widget.id);
  };

  const handleBotConfigClick = (event) => {
    event.stopPropagation();
    window.location.href = `/widget/bot-configuration?widgetId=${widget.id}${
      spaceId ? `&spaceId=${spaceId}` : ""
    }`;
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "N/A";
    return text.length <= maxLength
      ? text
      : text.substring(0, maxLength) + "...";
  };

  return (
    <Card
      sx={{
        cursor: "pointer",
        border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
        backgroundColor: isSelected ? "#f3f9ff" : "white",
        "&:hover": {
          boxShadow: 3,
          borderColor: isSelected ? "#1976d2" : "#c0c0c0",
        },
        transition: "all 0.2s ease-in-out",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Header with serial number and actions */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Chip
            label={`#${serialNumber}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Box display="flex" gap={0.5}>
            <Tooltip title="Edit Widget">
              <IconButton
                size="small"
                onClick={handleEditClick}
                sx={{
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.light",
                    color: "white",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="AI Bot">
              <IconButton
                size="small"
                onClick={handleBotConfigClick}
                sx={{
                  color: "secondary.main",
                  "&:hover": {
                    backgroundColor: "secondary.light",
                    color: "white",
                  },
                }}
              >
                <BotIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Widget">
              <IconButton
                size="small"
                onClick={handleDeleteClick}
                sx={{
                  color: "error.main",
                  "&:hover": { backgroundColor: "error.light", color: "white" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Widget Name */}
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 600,
            fontSize: "1.1rem",
            color: "text.primary",
            mb: 1,
            minHeight: "1.5em",
          }}
        >
          {widget.business_details?.display_name ||
            widget.business_details?.name ||
            "Unnamed Widget"}
        </Typography>

        {/* Business Type */}
        <Box mb={2}>
          <Chip
            label={getBusinessTypeName(widget.business_details?.business_type)}
            size="small"
            color="secondary"
            sx={{ fontSize: "0.75rem" }}
          />
        </Box>

        {/* Website URL */}
        {widget.business_details?.website_url && (
          <Box display="flex" alignItems="center" mb={2}>
            <WebsiteIcon
              sx={{ fontSize: 16, mr: 1, color: "text.secondary" }}
            />
            <Link
              href={widget.business_details.website_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: "0.875rem",
                color: "primary.main",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={(e) => e.stopPropagation()} // Prevent card selection
            >
              {truncateText(widget.business_details.website_url)}
            </Link>
          </Box>
        )}

        {/* Script ID */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              fontFamily: "monospace",
              backgroundColor: "#f5f5f5",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "0.75rem",
            }}
          >
            Script ID: {widget.script_id}
          </Typography>
        </Box>

        {/* Status Badge */}
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Chip
            label="Active"
            size="small"
            color="success"
            sx={{ fontSize: "0.75rem" }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default WidgetCard;
